import React, { useEffect, useRef, useState } from "react";
import { ApiDTO_PSFSform, ApiDTO_PSFSUI_ElementList, initialFormState, useGetPSFSCaseMutation, useGetPSFSElementsMutation, useSavePSFSCaseMutation } from "./Psfs_Slice";
import NTTCReview from "./NTTCReview";
import Dialog from "../NTSP_StatPortal/confirmdialog";
import { FoundationsButton } from "../../GenericComponents/button";
import TMPScheduling from "./TMPScheduling";
import TwoWeekSetUpCall from "./TwoWeekSetUpCall";
import OneWeekSetUpCall from "./OneWeekSetUpCall";
import OneTwoDayReminderCall from "./oneTwoDayReminderCall";
import QualityCheck from "./QualityCheck";
import { formatDateTimeLocal, getTimezoneOffset, tzCodeToIANA } from "../../Utils";
import moment from 'moment'
import 'moment-timezone';
import PatientHeader from "./PatientHeader";
import CommentBox, { useCommentBox } from "./Comments";
import { getNowInFacilityTimezone } from "../NTSP_StatPortal/NTSP_StatPortal";


export function convertEasternToLocal(
    dateStrInEastern: string | Date,
    tzCode?: string
): string | null {
    if (typeof dateStrInEastern !== 'string') {
        // If the input is a string, ensure it is in a valid format
        dateStrInEastern = dateStrInEastern.toString()
    }
    if (tzCode === 'America/New_York') {
        // If the time zone is already Eastern Time, we can return the date as is
        // return new Date(dateStrInEastern);
        return dateStrInEastern
    }
    if (!dateStrInEastern) return null
    const date = new Date(dateStrInEastern)
    const newYorkOffset = getTimezoneOffset('America/New_York', date)
    const utcTime = date.getTime() - newYorkOffset
    const localTime = new Date(utcTime + getTimezoneOffset(tzCode, date))
    return formatDateTimeLocal(localTime)
    // const utcDateTime = new Date(utcTime);
    // return formatDateTimeLocal(utcDateTime);
}

export const getAge = (dob: string): number => {
    const birthDate = new Date(dob)
    const now = new Date()

    if (birthDate > now) return 0 // prevent negative age

    const age = Math.floor((now.getTime() - birthDate.getTime()) / 31557600000) // avg year in ms
    return age
}

export function validateForm(
    form: any,
    schema: { [key: string]: (value: any, ntspElements?: ApiDTO_PSFSUI_ElementList) => string },
    ntspElements?: ApiDTO_PSFSUI_ElementList
) {
    const errors: { [key: string]: string } = {};
    Object.keys(schema).forEach((field) => {
        const error = schema[field](form, ntspElements);
        errors[field] = error;
    });
    return errors;
}

export type TabType =
    | "NTTCReview"
    | "TMPScheduling"
    | "TwoWeekSetUpCall"
    | "OneWeekSetUpCall"
    | "1-2DayReminderCall"
    | "QualityCheck";

export const tabTypes: TabType[] = [
    "NTTCReview",
    "TMPScheduling",
    "TwoWeekSetUpCall",
    "OneWeekSetUpCall",
    "1-2DayReminderCall",
    "QualityCheck",
];

/** 
 * Filters out elements with _key === -1 from PSFS element arrays
 * These are placeholder "Unknown" entries from the backend that should not be displayed in UI
 */
export function filterUnknownElements(elements: ApiDTO_PSFSUI_ElementList): ApiDTO_PSFSUI_ElementList {
    return {
        ...elements,
        TSP: elements.TSP?.filter(item => item._key !== -1) || [],
        YesNo: elements.YesNo?.filter(item => item._key !== -1) || [],
        TCC: elements.TCC?.filter(item => item._key !== -1) || [],
        PSFSStep: elements.PSFSStep?.filter(item => item._key !== -1) || [],
        PSFSStatus: elements.PSFSStatus?.filter(item => item._key !== -1) || [],
        Facilities: elements.Facilities?.filter(item => item._key !== -1) || [],
        PSFSTime: elements.PSFSTime?.filter(item => item._key !== -1) || [],
    }
}

declare let window: any;
const UnicornApp = window.unicornInstance as any;

const PSFS_StatPortal: React.FC<any> = (props) => {
    const [caseId, setCaseId] = useState<string | undefined>(props.caseId || undefined);
    const [FormGuid, setFormGuid] = useState<string>('')
    const [reload, setReload] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<TabType>("NTTCReview");
    const [form, setForm] = useState(initialFormState);
    const [errors, setErrors] = useState<{ [s: string]: string }>({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState<"clear" | "tab" | "load" | null>(null);
    const [pendingTab, setPendingTab] = useState<TabType | null>(null);
    const [tempId, setTempId] = useState<string>("");
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [SavePSFSCase, { error: savingError, isLoading: configLoading }] = useSavePSFSCaseMutation();
    const [GetPSFSElements] = useGetPSFSElementsMutation();
    const [psfsElements, setPsfsElements] = useState<ApiDTO_PSFSUI_ElementList | undefined>(undefined);
    const [isLoadingPSFSElements, setIsLoadingPSFSElements] = useState<boolean>(false);
    const [GetPSFSCase] = useGetPSFSCaseMutation();
    const [isCustomerSite] = useState<boolean>(UnicornApp.checkUserPrivileges("NTSPAdmin"))
    const [stepManuallyChanged, setStepManuallyChanged] = useState<{ [tab: string]: boolean }>({});
    const [resetFlag, setResetFlag] = useState(0);
    const [commentsReload, setCommentsReload] = useState(0);

    type PSFSCaseResponse = {
        FormData: { key: string; Value: Object }[]
        Symptoms?: any[]
    }

    const initialFormRef = useRef(form);
    const formRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        renderForm(form);
        // eslint-disable-next-line
    }, [activeTab]);

    useEffect(() => {
        setIsDirty(checkDirty());
        // eslint-disable-next-line
    }, [form]);

    const checkDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setDialogType(null);
        setPendingTab(null);
    };

    const handleClear = () => {
        setForm(initialFormState);
        initialFormRef.current = initialFormState;
        setCaseId(undefined);
        setTempId("");
        setFormGuid('');
        setResetFlag(f => f + 1);
        setActiveTab("NTTCReview");
        setErrors({});
        setIsSaved(false);
    };


    const handleDialogConfirm = async () => {
        setDialogOpen(false);
        if (dialogType === "load") {
            setCaseId(tempId);
            setTempId("");
            setIsSaved(true);
            formRef.current?.scrollIntoView({ behavior: "smooth" });
        } else if (dialogType === "clear") {
            handleClear();
            setIsSaved(false);
        } else if (dialogType === "tab" && pendingTab) {
            setActiveTab(pendingTab);
            setStepManuallyChanged({ [pendingTab]: true });
            const stepElement = psfsElements?.PSFSStep?.find(
                (step) => step.ETLMatchValue === pendingTab
            );

            // Create updated form with new step and status
            const updatedForm: ApiDTO_PSFSform = {
                ...form,
                fk_Step: stepElement?.StepNo,
                fk_Status: -1
            };

            // Update the form state
            setForm(updatedForm);

            // Save with the updated form directly (avoids stale closure issue)
            await handleSave(false, updatedForm);
        }
        setDialogType(null);
        setPendingTab(null);
    };

    const handleSave = async (close: boolean = false, formOverride?: ApiDTO_PSFSform) => {
        const userId = UnicornApp.globals?.me.get("id") || -1;

        // Use formOverride if provided, otherwise use form from state
        const formToSave = formOverride ?? form;

        const filteredForm: ApiDTO_PSFSform = Object.keys(initialFormState).reduce((acc, key) => {
            (acc as any)[key] = formToSave[key as keyof typeof formToSave];
            return acc;
        }, {} as ApiDTO_PSFSform);

        let combinedForm = {
            ...filteredForm,
            fk_UpdatedBy: userId,
            fk_CreatedBy: filteredForm.fk_CreatedBy === -1 ? userId : filteredForm.fk_CreatedBy,
            ts_RowCreated: filteredForm.ts_RowCreated ? filteredForm.ts_RowCreated :
             getNowInFacilityTimezone(form?.fk_Facility ?? -1, psfsElements, tzCodeToIANA),
            ts_RowUpdated: getNowInFacilityTimezone(form?.fk_Facility ?? -1, psfsElements, tzCodeToIANA),
        };

        delete (combinedForm as { ts_LKWDate?: string }).ts_LKWDate;
        delete (combinedForm as { ts_LKWTime?: string }).ts_LKWTime;
        delete (combinedForm as { ts_WakeUpStrokeDate?: string }).ts_WakeUpStrokeDate;
        delete (combinedForm as { ts_WakeUpStrokeTime?: string }).ts_WakeUpStrokeTime;

        const data: Array<{ key: string; Value: Object }> = Object.entries(combinedForm).map(
            ([key, value]) => ({
                key,
                Value: value as Object,
            })
        );

        const payload = { FormData: data, FormGuid };
        const response = await SavePSFSCase(payload); // Replace with actual API call

        if (response) {
            const statusCode = (response as any)?.data?.statusCode;
            if (statusCode !== undefined && statusCode !== 200) {
                UnicornApp.toastr(
                    "error",
                    "Saving Case Failed: Required fields missing or invalid"
                );
                return;
            }
            initialFormRef.current = {
                ...formToSave,
            };
            UnicornApp.toastr("success", "Case Saved Successfully");
            setCaseId(caseId);
            setIsSaved(true);
        } else {
            UnicornApp.toastr("error", "Saving Case Failed");
        }
    };

    /**
     * Centralized function to handle step, status, save, and tab navigation
     * @param stepName - The ETLMatchValue of the step (e.g., "NTTCReview", "TMPScheduling")
     * @param statusKey - The _key of the status to set
     * @param nextTab - Optional next tab to navigate to after save
     */
    const handleStepStatusAndSave = async (stepName: string, statusKey: number, nextTab?: TabType) => {
        // Find the step element by name

        let stepElement;
        if (nextTab) {
            stepElement = psfsElements?.PSFSStep?.find(
                (step) => step.ETLMatchValue === nextTab
            );
        } else {
            stepElement = psfsElements?.PSFSStep?.find(
                (step) => step.ETLMatchValue === stepName
            );
        }

        // Create updated form with new step and status
        const updatedForm: ApiDTO_PSFSform = {
            ...form,
            fk_Step: stepElement ? stepElement.StepNo : form.fk_Step,
            // fk_Step: stepElement ? stepElement.StepNo : form.fk_Step,
            fk_Status: statusKey
        };

        // Update the form state
        setForm(updatedForm);

        // Save with the updated form directly (avoids stale closure issue)
        await handleSave(false, updatedForm);

        // Navigate to next tab if provided
        if (nextTab) {
            setActiveTab(nextTab);
        }
    };

    const handleStatusResetAndClose = async (stepName: string, statusKey: number, nextTab?: TabType) => {
        // Find the step element by name    
        await handleStepStatusAndSave(stepName, statusKey, nextTab);
        setForm(initialFormState);
        window.loadPSFSCase = undefined;
        setCaseId(undefined);
        setFormGuid('');
        setResetFlag(f => f + 1);
        window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll to top
    }

    const renderForm = (form: ApiDTO_PSFSform) => {
        const tabProps = {
            form,
            setForm,
            handleSave,
            handleStepStatusAndSave,
            PSFSElements: psfsElements,
            setActiveTab,
            stepManuallyChanged: stepManuallyChanged[activeTab] || false,
            handleStatusResetAndClose
        };
        if (isLoadingPSFSElements) {
            return <span className="ntsp-form-container"><div id='ajax-loading-box-attr' className="ajax-loading-box show"><span
                className="ajax-loading-image"></span>
                Loading...
            </div></span>
        }
        switch (activeTab) {
            case "NTTCReview":
                return <NTTCReview {...tabProps} />;
            case "TMPScheduling":
                return <TMPScheduling {...tabProps} />;
            case "TwoWeekSetUpCall":
                return <TwoWeekSetUpCall {...tabProps} />;
            case "OneWeekSetUpCall":
                return <OneWeekSetUpCall {...tabProps} />;
            case "1-2DayReminderCall":
                return <OneTwoDayReminderCall {...tabProps} />;
            case "QualityCheck":
                return <QualityCheck {...tabProps} />;
            default:
                return null;
        }
    };

    useEffect(() => {
        window.loadPSFSCase = (caseId: string, formGuid: string, tabno?: number) => {
            setTempId('')
            setTempId(caseId)
            setCaseId(caseId)
            setFormGuid(formGuid)
            // If tabno is provided, set the active tab accordingly
            if (tabno !== undefined) {
                switch (tabno) {
                    case 1:
                        setActiveTab('NTTCReview')
                        break
                    case 2:
                        setActiveTab('TMPScheduling')
                        break
                    case 3:
                        setActiveTab('TwoWeekSetUpCall')
                        break
                    case 4:
                        setActiveTab('OneWeekSetUpCall')
                        break
                    case 5:
                        setActiveTab('1-2DayReminderCall')
                        break
                    case 6:
                        setActiveTab('QualityCheck')
                        break
                    default:
                        setActiveTab('NTTCReview')
                }
            }
            // Always scroll to the portal when loading a case
            formRef.current?.scrollIntoView({ behavior: "smooth" });
            setReload(f => !f) // Trigger reload to fetch new case data
        }
        setIsLoadingPSFSElements(true)
        GetPSFSElements()
            .unwrap()
            .then((response) => {
                if (response !== null) {
                    //Filter out Unknown elements (_key === -1) before setting state
                    const filteredResponse = filterUnknownElements(response)
                    setPsfsElements(filteredResponse);

                }
                setIsLoadingPSFSElements(false)
            })
            .catch((error) => {
                console.error('Error:', error)
                setIsLoadingPSFSElements(false)
            })
    }, [resetFlag])

    useEffect(() => {
        if (caseId != null && caseId !== '') {
            setErrors({})
            GetPSFSCase({ caseId, FormGuid })
                .unwrap()
                .then((response: PSFSCaseResponse) => {
                    if (response !== null) {
                        // Convert response.FormData (Array<{ key: string, Value: any }>) to ApiDTO_PSFSform
                        const formDataObj: ApiDTO_PSFSform = Array.isArray(response.FormData)
                            ? response.FormData.reduce((acc: any, item: any) => {
                                acc[item.key] = item.Value
                                return acc
                            }, {} as ApiDTO_PSFSform)
                            : {}

                        // Keep dates in Eastern timezone format (no conversion needed)
                        // PSFS always works in Eastern timezone, dates are already in correct format from backend
                        formDataObj.ts_Appointment = formDataObj.ts_Appointment
                            ? formDataObj.ts_Appointment.toString()
                            : ''
                        formDataObj.ts_DeliveryDate = formDataObj.ts_DeliveryDate
                            ? formDataObj.ts_DeliveryDate.toString()
                            : ''

                        // If FullSSN is not available, use PatientSSN
                        if (!formDataObj.FullSSN && formDataObj.PatientSSN) {
                            formDataObj.FullSSN = formDataObj.PatientSSN;
                        }

                        formDataObj.ts_RowCreated = formDataObj.ts_RowCreated
                            ? convertEasternToLocal(
                                  formDataObj.ts_RowCreated.toString(),
                                  formDataObj.tzCode?.toString()
                              )
                            : ''
                        formDataObj.ts_RowUpdated = formDataObj.ts_RowUpdated
                            ? convertEasternToLocal(
                                  formDataObj.ts_RowUpdated.toString(),
                                  formDataObj.tzCode?.toString()
                              )
                            : ''

                        Object.keys(formDataObj).forEach((key) => {
                            if ((formDataObj as any)[key] === null) {
                                ; (formDataObj as any)[key] =
                                    initialFormState[key as keyof ApiDTO_PSFSform]
                            }
                        })

                        setForm({
                            ...formDataObj,
                        })
                        initialFormRef.current = formDataObj // Update initial form state when changing case Id

                        // setForm(prev => ({
                        //     ...prev,
                        //     ...formDataObj,
                        //     StaffFullName: [formDataObj.StaffFirstName, formDataObj.StaffLastName].filter(Boolean).join(" "),
                        // }));

                        // TODO handle initial symptom selection
                        if (Array.isArray(response.Symptoms)) {
                            // const befastSymptoms: number[] = [];
                            // const largeVesselSymptoms: number[] = [];
                            // response.Symptoms.forEach((symptom: any) => {
                            //     if (symptom.fk_SymptomType === 1) {
                            //         befastSymptoms.push(symptom.fk_Symptom);
                            //     } else if (symptom.fk_SymptomType === 2) {
                            //         largeVesselSymptoms.push(symptom.fk_Symptom);
                            //     }
                            // });

                            // setForm(prev => ({
                            //     ...prev,
                            //     BefastSymptoms: befastSymptoms,
                            //     LargeVesselSymptoms: largeVesselSymptoms,
                            // }));
                            initialFormRef.current = {
                                ...formDataObj,
                                // TODO
                                // BefastSymptoms: befastSymptoms,
                                // LargeVesselSymptoms: largeVesselSymptoms,
                            }
                        }
                        //if (activeTab === 'PatientDetails') setActiveTab('NTTCReview')
                        //setFormActive(true);

                        const timer = setTimeout(() => {
                            setCommentsReload(c => c + 1);
                        }, 200);

                        return () => clearTimeout(timer); // cleanup if reload changes quickly
                    }
                })
                .catch((error) => {
                    // when api is forbidden we do not get the response instead it goes to catch block
                    if (error.status === 403) {
                        UnicornApp.toastr('error', 'You do not have permission to view the case.')
                    } else {
                        UnicornApp.toastr('error', 'An unexpected error occurred.')
                    }
                    setForm(initialFormState)
                    setCaseId(undefined)
                    setActiveTab('NTTCReview')
                })
        }
    }, [caseId, reload])



    useEffect(() => {
        if (!!tempId && !!form.CaseId && checkDirty()) {
            setDialogType('load')
            // setDialogOpen(true)

        } else if (!!tempId) {
            setCaseId(tempId)
            setIsSaved(true)
            formRef.current?.scrollIntoView({ behavior: 'smooth' })
        }
    }, [tempId])

    const openChangeStepDialog = () => {
        setDialogType("tab");
        setDialogOpen(true);
    };

    const commentBoxMinHeights: Record<TabType, string> = {
        NTTCReview: "615px",
        TMPScheduling: "433px",
        TwoWeekSetUpCall: "611px",
        OneWeekSetUpCall: "611px",
        "1-2DayReminderCall": "162px",
        QualityCheck: "124px"
    };

    const { commentBoxProps } = useCommentBox({ form, setForm, caseId: form?.CaseId, commentsReload });
    return (
        <span ref={formRef}>
            <div className="tab_section tab-change-btn">
                {caseId && !isLoadingPSFSElements ? (
                    <>
                        <span className="ntsp_tab-buttons w-100 ">
                            {psfsElements?.PSFSStep.sort((a, b) => (a.StepNo ?? 0) - (b.StepNo ?? 0)).map((tab, idx) => (
                                <button
                                    key={tab.StepNo}
                                    id={`ntsp_tab_${tab.ETLMatchValue}`}
                                    type="button"
                                    title={
                                        !form.CaseId
                                            ? "Create or select case to view details"
                                            : tab.ETLMatchValue.replace(/([A-Z])/g, " $1").trim()
                                    }
                                    className={`py-3 ntsp_tab-button ${activeTab === tab.ETLMatchValue ? "active" : ""}`}
                                    onClick={() => {
                                        setActiveTab(tab.ETLMatchValue as TabType);
                                        setStepManuallyChanged({ [tab.ETLMatchValue]: false });
                                    }}
                                >
                                    {`${tab.StepNo}. ${tab.FriendlyName}`}
                                </button>
                            ))}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span
                                className=""
                                style={{ display: 'flex', alignItems: 'end', flexDirection: 'column' }}
                            >
                                <h6 style={{ whiteSpace: 'nowrap', color: '#00ae00', marginBottom: 0, marginInline: '0.5rem' }}>
                                    {
                                        (() => {
                                            const facility = psfsElements?.Facilities?.find(
                                                (c: { _key: any; FriendlyName?: string; TZCode?: string }) => c._key === form.fk_Facility
                                            );
                                            const facilityName = facility?.FriendlyName || '';
                                            const ianaTz = facility?.TZCode ? tzCodeToIANA(facility.TZCode) : '';
                                            const now = ianaTz ? moment.tz(new Date(), ianaTz) : moment(new Date());
                                            const tzAbbr = ianaTz ? now.format('z') : '';
                                            const timeStr = now.format('HH:mm');
                                            return `${facilityName}${timeStr && tzAbbr ? ` (${timeStr} ${tzAbbr})` : ''}`;
                                        })()
                                    }
                                </h6>
                                <p style={{ fontSize: '10px', width: 'max-content' }}>All times in this form are in Eastern time</p>
                            </span>
                            <div>
                                {isCustomerSite && (
                                    <FoundationsButton
                                        customStyle={{ margin: '0.5rem' }}
                                        id="psfs_new_case"
                                        buttonClasses="psfs-gray-button"
                                        onClick={openChangeStepDialog}
                                    >
                                        Change Current Step
                                    </FoundationsButton>
                                )}
                            </div>
                        </div>
                    </>
                ) :
                    (<div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            minHeight: '400px',
                            padding: '2rem'
                        }}>{isLoadingPSFSElements ? 'Loading...' : 'Please select a case from the worklist'}</div>
                    )}
            </div>
            {caseId && !isLoadingPSFSElements && (
                <PatientHeader
                    psfsElements={psfsElements}
                    form={form}
                    stepManuallyChanged={stepManuallyChanged[activeTab] || false}
                />)}
            {caseId && !isLoadingPSFSElements && (
                <div className="psfs-tab-commentbox">
                    <span className="psfs-tab-commentbox-left" >
                        {caseId && renderForm(form)}
                    </span>
                    <span className="psfs-tab-commentbox-right" >
                        {caseId && (
                            <CommentBox
                                key={String(caseId) + String(commentsReload)}
                                {...commentBoxProps}
                                minHeight={commentBoxMinHeights[activeTab] || "445px"}
                            />
                        )}
                    </span>
                </div>
            )}
            <Dialog
                isOpen={dialogOpen}
                title="Change Current Step"
                height={246}
                width={346}
                className="ui-front ui-dialog-buttons left-align-text"
                buttons={[
                    {
                        label: "Cancel",
                        className: "ui-button ui-corner-all ui-widget app-button negative",
                        onClick: handleDialogCancel,
                    },
                    {
                        label: "Save Changes",
                        className: "ui-button ui-corner-all ui-widget app-button",
                        onClick: handleDialogConfirm,
                    },
                ]}
                onClose={handleDialogCancel}
                hideClose={false}
            >
                <div className="tab_change_dialog_content">
                    <p className="content_subtitle">Update the current step for this Case</p>
                    <div className="form-group Change-step-select">
                        <label htmlFor="fk_Outcome_2 ml-3">Step:</label>
                        <select
                            className="ntsp-fields"
                            style={{ borderRadius: "5px" }}
                            id="fk_Outcome_2"
                            name="pendingTab"
                            value={
                                pendingTab ??
                                (() => {
                                    const steps = psfsElements?.PSFSStep || [];
                                    if (!Array.isArray(steps) || steps.length === 0) return 'Unknown';
                                    const stepNo = stepManuallyChanged
                                        ? form.fk_Step
                                        // : Math.min(form.fk_Step + 1, maxStep);
                                        : form.fk_Step;
                                    const stepObj = steps.find((c) => c.StepNo !== undefined && c.StepNo === stepNo);
                                    return stepObj?.ETLMatchValue || 'Unknown';
                                })()
                            }
                            onChange={e => setPendingTab(e.target.value as TabType)}
                            tabIndex={1}
                        >
                            {psfsElements?.PSFSStep?.map((Step) => (
                                <option key={Step.StepNo} value={Step.ETLMatchValue}>
                                    {Step.FriendlyName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </Dialog>
        </span>
    );
};

export default PSFS_StatPortal;